import CartaFsClient from "@contextualize/cartafs/dist/CartaFsClient";
import ChunkedArray from "library/ChunkedArray";
import { FailedFile, UploadFile } from "./FileChips";
import { CreateFile, Key } from "@contextualize/cartafs/dist/types/file";
import { FileResolution, Resolution } from "./MatchingFileDialog";
import Mutex from "library/Mutex";
import { createContext } from "react";
import { MovingMedian, ProgressTracker, SimpleMovingAverage } from "@contextualize/cartafs/dist/util/Estimator";
import { InitUploadResponse } from "@contextualize/cartafs/dist/types/dto";

const defaultUploadTime = 59 * 60 + 59 * 1000;

export const CartaFsContext = createContext<{cartaFs: CartaFsClient | undefined}>({cartaFs: undefined});

interface ResolvedFile{
    init: InitUploadResponse;
    name: string;
    file: File;
    resolution: Resolution;
    error?: string;
}

async function processFileResolution(file: FileResolution, existingFiles: FileUriData[], renameMutex: Mutex, cartaFs: CartaFsClient, formContext: { projectId: string }): Promise<ResolvedFile> {
    switch (file.resolution) {
        case Resolution.Create:
            existingFiles.push({
                name: file.file.name,
                meta: { file: "" },
                type: ""
            })
            return {
                init: await cartaFs.initiateUpload({
                    filename: file.file.name,
                    project: formContext.projectId,
                }, file.file),
                name: file.file.name,
                file: file.file,
                resolution: file.resolution
            }
        case Resolution.Keep:
            let name = file.file.name
            let copy = 1;
            const nameSplit = name.split('.');
            const first = nameSplit[0];
            let match = /\((\d+)\)$/.exec(first);
            let basename = nameSplit[0];
            console.log(match);
            if (match && match[1]) {
                copy = parseInt(match[1]) + 1;
                basename = first.slice(0, -match[0].length)
            }

            await renameMutex.take()
            const findName = (f: FileUriData) => f.name === name;

            do {
                nameSplit[0] = `${basename}(${copy})`;
                name = nameSplit.join('.');
                copy++;
            } while (existingFiles.find(findName))

            existingFiles.push({
                name: name,
                meta: { file: "" },
                type: ""
            })
            renameMutex.release();

            return {
                init: await cartaFs.initiateUpload({
                    filename: name,
                    project: formContext.projectId
                }, file.file),
                name: file.file.name,
                file: file.file,
                resolution: file.resolution
            }
        case Resolution.Replace:
            const existing = file.match?.meta;
            if (!existing) {
                throw new Error("This shouldn't be possible");
            }
            return {
                init: await cartaFs.initiateUpdate(existing, file.file),
                name: file.file.name,
                file: file.file,
                resolution: file.resolution
            }
        default:
            throw new Error("Should not have come here")
    }
}

export async function uploadFile(multiple: boolean, cartaFs: CartaFsClient | undefined, formContext: { projectId: string } | null, existing: string | string[], files?: FileResolution[] | null, setUploading?: React.Dispatch<UploadFile[]>, setErrorFiles?: React.Dispatch<FailedFile[]>): Promise<string[] | string> {
    if (!cartaFs || !formContext) {
        throw new Error("Error loading document context");
    }
    if (!files) {
        throw new Error("No documents submitted");

    }

    if (!multiple && files.length !== 1) {
        throw new Error("Must select just one file");
    }

    const filesArr = files.filter(f => f.resolution !== Resolution.Skip);

    // Get the uploading status infront of the user ASAP
    if (setUploading) {
        setUploading(filesArr.map((file, index) => {
            return {
                href: `${index}`,
                name: file.file.name,
                progress: 0, 
                estimate: {
                    etaCalc: new ProgressTracker(0),
                    estimateBuffer: new SimpleMovingAverage(0, 0),
                    eta: new Date(Date.now() + defaultUploadTime),
                }
            }
        }));
    }


    const existingFiles = ([] as string[]).concat(existing).filter(a => !!a).map(a => FileUri.extract(a));
    let renameMutex = new Mutex();

    // Prefire the upload
    const initiated = await ChunkedArray.from(filesArr, 5).map(async (file: FileResolution) => {
        try{
            return await processFileResolution(file, existingFiles, renameMutex, cartaFs, formContext);
        }catch(e){
            const error = e as Error;
            return {error: error.message, name: file.file.name}
        }
    });

    let failedResolutions: FailedFile[] = [];

    if (setUploading) {
        let finished = false;
        const resolvedFiles = initiated.flatten().map<UploadFile | FailedFile>((resolved) => {
            if('error' in resolved){
                return {
                    name: resolved.name,
                    error: `Error encountered when prepairing file for upload:\n${resolved.error}`,
                    href: '',
                    progress: -1,
                    estimate: {etaCalc: new ProgressTracker(100), estimateBuffer: new MovingMedian(0,0), eta: new Date()}
                }
            }
            
            return {
                name: resolved.init.meta.stats.filename,
                href: resolved.init.meta.file,
                progress: 10,
                estimate: {
                    etaCalc: new ProgressTracker(100, {defaultProgress: 10, bucketing: 1000}),
                    estimateBuffer: new MovingMedian(10, new Date(Date.now() + defaultUploadTime).getTime()),
                    eta: new Date(Date.now() + defaultUploadTime),
                }
            }
        })

        failedResolutions = resolvedFiles.filter((file)=>(('error' in file))) as FailedFile[];
        const successfulResolutions: UploadFile[] = resolvedFiles.filter((file)=>(!('error' in file))) as UploadFile[];

        setErrorFiles?.(failedResolutions);

        const progresses = successfulResolutions.filter(((file)=>(!('error' in file)))).reduce((previous, current) => {
            previous[current.href] = current;
            return previous;
        }, {} as Record<string, UploadFile>)

        setUploading(Object.values(progresses));

        const updateStatus = (file: string) => {
            return (progress: number) => {
                if (finished) return;

                progresses[file].progress = 10 + progress * 90;

                if(progresses[file].estimate.etaCalc.canUpdate()){
                    const eta = progresses[file].estimate.etaCalc.getETA(progresses[file].progress);
                    progresses[file].estimate.estimateBuffer.addSample(eta.getTime());
                    const estimate = progresses[file].estimate.estimateBuffer.getEstimate();
                    progresses[file].estimate.eta = new Date(estimate);

                }
                setUploading(Object.values(progresses));
            }
        }

        await initiated.forEach(async (resolved) => {
            if('error' in resolved){
                return;
            }
            const {init, file} = resolved;
            try{
                await cartaFs.completeUpload(init.meta, init.uploadUrl, file, updateStatus(init.meta.file));
            }catch(e){
                const error = e as Error;
                resolved.error = `Error encountered during upload:\n${error.message}`;
                failedResolutions.push({name: resolved.name, error: resolved.error});
            }
        });

        finished = true;
        setUploading([]);
    } else {
        initiated.forEach(async (resolved) => {
            if('error' in resolved){
                return;
            }
            const {init, file} = resolved;
            try{
                await cartaFs.completeUpload(init.meta, init.uploadUrl, file);
            }catch(e){
                const error = e as Error;
                resolved.error = `Error encountered during upload:\n${error.message}`;
                failedResolutions.push({name: resolved.name, error: resolved.error});
            }
        })
    }

    setErrorFiles?.(failedResolutions);
    let appendFiles = initiated.flatten().filter(file=>!file.error) as ResolvedFile[];

    if(multiple){
        appendFiles = appendFiles.filter(f=>f.resolution !== Resolution.Replace)
    }

    const fileIds = appendFiles.map(({ init, file }) => FileUri.to(init.meta, file));

    if (!multiple) {
        return fileIds[0];
    } else {
        return fileIds.concat(existing);
    }
}

export async function downloadFile(cartaFs: CartaFsClient | undefined, fileKey: string) {
    if (!cartaFs) {
        throw new Error("Error loading document context");
    }
    const fileDownlaod = await cartaFs.download(fileKey);
    const link = document.createElement('a');
    link.href = fileDownlaod.downloadUrl;
    link.download = fileDownlaod.meta.stats.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export interface FileUriData {
    type: string;
    name: string;
    meta: { file: string };
}

export class FileUri {
    private constructor() { };

    static to(meta: CreateFile & Key, file: File) {
        return `data:${file.type};name=${meta.stats.filename};cartafs,${meta.file}`
    }

    static extract(uri: string): FileUriData {
        const [type, name, encoding] = uri.split(';');
        return {
            type,
            name: name.split('=')[1],
            meta: { file: encoding.split(',')[1] }
        }
    }
}
